import {
  ChartBarIcon,
  CommandLineIcon,
  DocumentChartBarIcon,
  HomeIcon,
  SparklesIcon,
  UserGroupIcon,
  VideoCameraIcon,
} from "@heroicons/react/24/outline";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/solid";
import React, { useMemo } from "react";
import { Link, useLocation } from "react-router-dom";

import { useUIContext } from "../../contexts/UIContext";
import { REBOOT_LIVE_ACCESS_PERMISSION } from "../../features/RebootLive/constants";
import { useAppSelector } from "../../redux/hooks";
import { selectUserPermissions } from "../../redux/slices/userSlice";
import { joinClassNames } from "../../shared/Functions";
import {
  SIDEBAR_NAV_ITEMS,
  type SidebarNavItemData,
} from "../../shared/sidebarNavItems";
import { VERSION } from "../../version";
import type { HeroIcon } from "../../types";

const ICON_MAP: Record<string, HeroIcon> = {
  Home: HomeIcon,
  Reports: DocumentChartBarIcon,
  Sessions: VideoCameraIcon,
  Players: UserGroupIcon,
  Analyze: ChartBarIcon,
  Develop: CommandLineIcon,
  "Reboot Live": SparklesIcon,
};

interface SidebarNavItem extends SidebarNavItemData {
  icon: HeroIcon;
}

const sidebarNavItemsWithIcons: SidebarNavItem[] = SIDEBAR_NAV_ITEMS.map(
  (item) => ({
    ...item,
    icon: ICON_MAP[item.name] ?? HomeIcon,
  }),
);

const pathMatches = (
  pathname: string,
  item: { name: string; href: string },
) => {
  if (item.href === "/") return pathname === "/";
  if (item.name === "Reports")
    return (
      pathname.includes("/reports") ||
      pathname.includes("/aggregate-group/") ||
      pathname.includes("/request-analysis")
    );
  if (item.name === "Sessions")
    return (
      pathname.includes("/session") ||
      pathname.includes("/processing-sessions") ||
      pathname.includes("/completed-sessions")
    );
  if (item.name === "Players")
    return pathname.includes("/players") || pathname.includes("/player/");
  if (item.name === "Analyze") return pathname.includes("/analyze");
  if (item.name === "Develop") return pathname.includes("/develop");
  if (item.name === "Reboot Live") return pathname === "/reboot-live";
  return pathname.startsWith(item.href);
};

export const DesktopSidebar: React.FC = () => {
  const { loadingDashboard, dashboardError } = useUIContext();
  const navDisabled = loadingDashboard || dashboardError;
  const showSkeletons = loadingDashboard && !dashboardError;
  const permissions = useAppSelector(selectUserPermissions);

  const location = useLocation();

  const navigation = useMemo(
    () =>
      sidebarNavItemsWithIcons
        .filter(
          (item) =>
            item.name !== "Reboot Live" ||
            permissions.includes(REBOOT_LIVE_ACCESS_PERMISSION),
        )
        .map((item) => ({
          ...item,
          current: pathMatches(location.pathname, item),
        })),
    [location.pathname, permissions],
  );

  return (
    <div
      className="h-full hidden lg:flex sm:w-24 z-10"
      data-testid="desktop-sidebar-component"
    >
      <div className="flex flex-col w-24 h-full fixed">
        <div className="flex flex-col flex-grow pt-1 pb-4 overflow-y-auto bg-primary-800">
          <nav
            className="mt-2 flex-1 flex flex-col justify-between overflow-y-auto"
            aria-label="Sidebar"
          >
            <div
              className="px-2 space-y-1"
              data-testid={navDisabled ? "desktop-sidebar-skeleton" : undefined}
            >
              {navigation.map((item) => {
                const itemSlug = item.name.toLowerCase().replace(/\s+/g, "-");
                const iconFlipClass = item.flipIcon ? "scale-x-[-1]" : "";
                if (showSkeletons) {
                  return (
                    <div
                      key={item.name}
                      className="group flex flex-col items-center px-2 py-2 text-sm leading-6 font-medium rounded-md text-white text-center opacity-50 pointer-events-none"
                      data-testid={`desktop-sidebar-${itemSlug}-skeleton`}
                    >
                      <item.icon
                        className={joinClassNames(
                          "flex-shrink-0 h-6 w-6 text-white animate-pulse",
                          iconFlipClass,
                        )}
                        data-testid={`desktop-sidebar-${itemSlug}-icon-skeleton`}
                      />
                      <p className="animate-pulse">{item.name}</p>
                    </div>
                  );
                }
                if (dashboardError) {
                  return (
                    <div
                      key={item.name}
                      className="group flex flex-col items-center px-2 py-2 text-sm leading-6 font-medium rounded-md text-white text-center opacity-50 cursor-not-allowed"
                      aria-disabled="true"
                      data-testid={`desktop-sidebar-${itemSlug}-skeleton`}
                    >
                      <item.icon
                        className={joinClassNames(
                          "flex-shrink-0 h-6 w-6 text-white",
                          iconFlipClass,
                        )}
                        aria-hidden="true"
                        data-testid={`desktop-sidebar-${itemSlug}-icon`}
                      />
                      {item.name}
                    </div>
                  );
                }
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={joinClassNames(
                      item.current ? "bg-gray-500" : "hover:bg-gray-500",
                      "group flex flex-col items-center px-2 py-2 text-sm leading-6 font-medium rounded-md text-white text-center",
                    )}
                    aria-current={item.current ? "page" : undefined}
                    id={`desktop-sidebar-${itemSlug}-link`}
                    data-testid={`desktop-sidebar-${itemSlug}-link`}
                  >
                    <item.icon
                      className={joinClassNames(
                        "flex-shrink-0 h-6 w-6 text-white",
                        iconFlipClass,
                      )}
                      aria-hidden="true"
                      data-testid={`desktop-sidebar-${itemSlug}-icon`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </div>
            <div className="px-2 pb-16 space-y-1" data-testid="help-container">
              <a
                className="group flex flex-col items-center px-2 py-2 text-sm leading-6 font-medium rounded-md text-white"
                href="#"
                onClick={() => window.Beacon("open")}
                data-testid="helpscout-link"
              >
                <QuestionMarkCircleIcon
                  className="flex-shrink-0 h-8 w-8 text-white"
                  data-testid="help-icon"
                />
              </a>
              <p
                className="text-xs text-gray-300 text-center"
                data-testid="version-number"
              >
                {VERSION}
              </p>
            </div>
          </nav>
        </div>
      </div>
    </div>
  );
};
